import { Route, Routes } from 'react-router-dom';

import { AuthProvider } from './context/AuthContext';
import PrivateRoute from './utils/PrivateRoute';
import TwoFactorRoute from './utils/TwoFactorRoute';
import { PermissionProvider } from './context/PermissionContext';
import { NotificationProvider } from './context/NotificationContext';
import { FormulationsTabProvider } from './context/FormulationsTabContext';
import { DrawerProvider } from './context/DrawerContext';
import { MaterialsCaptureProvider } from './context/MaterialsCaptureContext';
import { LipidRawMaterialProvider } from './context/materialcapture/LipidRawMaterialContext';
import { BufferProvider } from './context/materialcapture/BufferContext';
import { PayloadRawMaterialProvider } from './context/materialcapture/PayloadRawMaterialContext';
import { LipidStocksProvider } from './context/materialcapture/LipidStocksContext';
import { PayloadPhaseProvider } from './context/materialcapture/PayloadPhaseContext';
import { EncapsulantPhaseProvider } from './context/materialcapture/EncapsulantPhaseContext';
import { MixingProvider } from './context/materialcapture/MixingContext';
import { DilutionProvider } from './context/materialcapture/DilutionContext';
import { FinalLNPSampleProvider } from './context/materialcapture/FinalLNPSampleContext';
import { FormulationPickerProvider } from './context/materialcapture/FormulationPickerContext';
import HealthCheck from './utils/HealthCheck';

// Analytics
import DashboardPage from './pages/analytics/Dashboard';
import ImportDataPage from './pages/analytics/ImportData';
import QueryPage from './pages/analytics/Query';
import S3NavigatorPage from './pages/analytics/S3Navigator';
import FormulationsPage from './pages/analytics/Formulations';
import LNPSampleDetailPage from './pages/analytics/LNPSampleDetail';
import AQControlDetailPage from './pages/analytics/AQControlDetail';
import TransfectionControlDetailPage from './pages/analytics/TransfectionControlDetail';
import EmptyWellsPage from './pages/analytics/EmptyWells';
import QualityControlPage from './pages/qualitycontrol/QualityControl';
import QualityControlDetailPage from './pages/qualitycontrol/QualityControlDetail';
import TableListPage from './pages/analytics/TableList';
import DatabaseStatisticsPage from './pages/analytics/DatabaseStatistics';
import PlateAnalysisStatusPage from './pages/analytics/PlateAnalysisStatus';

// Materials Capture
import MaterialsCapturePage from './pages/analytics/MaterialsCapture';
import MaterialCaptureHomePage from './pages/materialcapture/MaterialCaptureHome';
// Lipid Raw Materials
import LipidRawMaterialListPage from './pages/materialcapture/LipidRawMaterials/LipidRawMaterialList';
import LipidRawMaterialCreatePage from './pages/materialcapture/LipidRawMaterials/LipidRawMaterialCreate';
import LipidRawMaterialEditPage from './pages/materialcapture/LipidRawMaterials/LipidRawMaterialEdit';
// Buffers
import BuffersListPage from './pages/materialcapture/Buffers/BuffersList';
import BufferCreatePage from './pages/materialcapture/Buffers/BufferCreate';
import BufferEditPage from './pages/materialcapture/Buffers/BufferEdit';
// Payload Raw Materials
import PayloadRawMaterialsListPage from './pages/materialcapture/PayloadRawMaterials/PayloadRawMaterialsList';
import PayloadRawMaterialsCreatePage from './pages/materialcapture/PayloadRawMaterials/PayloadRawMaterialsCreate';
import PayloadRawMaterialsEditPage from './pages/materialcapture/PayloadRawMaterials/PayloadRawMaterialsEdit';
// Lipid Stocks 
import LipidStocksListPage from './pages/materialcapture/LipidStocks/LipidStocksList';
import LipidStocksCreatePage from './pages/materialcapture/LipidStocks/LipidStocksCreate';
import LipidStocksEditPage from './pages/materialcapture/LipidStocks/LipidStocksEdit';
// Payload Phase
import PayloadPhaseListPage from './pages/materialcapture/PayloadPhase/PayloadPhaseList';
import PayloadPhaseCreatePage from './pages/materialcapture/PayloadPhase/PayloadPhaseCreate';
import PayloadPhaseEditPage from './pages/materialcapture/PayloadPhase/PayloadPhaseEdit';
// Encapsulant Phase
import EncapsulantPhaseCreatePage from './pages/materialcapture/EncapsulantPhase/EncapsulantPhaseCreate';
// Mixing
import MixingPage from './pages/materialcapture/Mixing/Mixing';
// Dilution
import DilutionCreatePage from './pages/materialcapture/Dilution/DilutionCreate';
// Final LNP Sample
import FinalLNPSampleCreatePage from './pages/materialcapture/FinalLNPSample/FinalLNPSampleCreate';

// Account
import LoginPage from './pages/account/Login';
import ForgotPasswordPage from './pages/account/ForgotPassword';
import ResetPasswordPage from './pages/account/ResetPassword';
import UserProfilePage from './pages/account/UserProfile';
import EmailVerificationPage from './pages/account/EmailVerification';

//User Management
import RoleListPage from './pages/user_mngmnt/RoleList';
import RoleCreatePage from './pages/user_mngmnt/RoleCreate';
import RoleEditPage from './pages/user_mngmnt/RoleEdit';
import UserListPage from './pages/user_mngmnt/UserList';
import LogListPage from './pages/user_mngmnt/LogList';
import OrganisationsListPage from './pages/user_mngmnt/OrganisationsList';
import OrganisationEditPage from './pages/user_mngmnt/OrganisationEdit';
import FileTypeListPage from './pages/user_mngmnt/FileTypeList';
import FileTypeEditPage from './pages/user_mngmnt/FileTypeEdit';
import SettingsPage from './pages/user_mngmnt/Settings';
import ProjectListPage from './pages/user_mngmnt/ProjectList';

//Common
import PageNotFoundPage from './pages/common/PageNotFound';
import NotificationPage from './pages/common/Notification';

const App = () => {
  return (
    <>
      <Routes>
        <Route path='/login' element={<AuthProvider><LoginPage /></AuthProvider>} />
        <Route path='/forgot-password' element={<ForgotPasswordPage />} />
        <Route path='/reset-password/:code' element={<ResetPasswordPage />} />
        <Route path='/page-not-found' element={<PageNotFoundPage />} />
        <Route path='/health' element={<HealthCheck />} />

        <Route path='/emailverification' element={
          <AuthProvider>
            <TwoFactorRoute Component={EmailVerificationPage} />
          </AuthProvider>}
        />

        <Route exact path='/' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <PrivateRoute Component={DashboardPage} />
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/import' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <PrivateRoute Component={ImportDataPage} />
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/query' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <PrivateRoute Component={QueryPage} />
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/quality/control' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <PrivateRoute Component={QualityControlPage} />
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/quality/control/plate/:id' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <PrivateRoute Component={QualityControlDetailPage} />
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/plate-status' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <PrivateRoute Component={PlateAnalysisStatusPage} />
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/s3-navigator' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <PrivateRoute Component={S3NavigatorPage} />
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/formulations' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <FormulationsTabProvider>
                    <PrivateRoute Component={FormulationsPage} />
                  </FormulationsTabProvider>
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route path='/lnpsample/detail/:id' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <FormulationsTabProvider>
                    <PrivateRoute Component={LNPSampleDetailPage} />
                  </FormulationsTabProvider>
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route path='/aqcontrol/detail/:id' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <FormulationsTabProvider>
                    <PrivateRoute Component={AQControlDetailPage} />
                  </FormulationsTabProvider>
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route path='/transfectioncontrol/detail/:id' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <FormulationsTabProvider>
                    <PrivateRoute Component={TransfectionControlDetailPage} />
                  </FormulationsTabProvider>
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route path='/empty/wells/detail/:id' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <FormulationsTabProvider>
                    <PrivateRoute Component={EmptyWellsPage} />
                  </FormulationsTabProvider>
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/materials-capture' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  {/* <MaterialsCaptureProvider> */}
                  <PrivateRoute Component={MaterialCaptureHomePage} />
                  {/* </MaterialsCaptureProvider> */}
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/materials-capture/old' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <MaterialsCaptureProvider>
                    <PrivateRoute Component={MaterialsCapturePage} />
                  </MaterialsCaptureProvider>
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/lipid-raw-materials' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <PrivateRoute Component={LipidRawMaterialListPage} />
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/lipid-raw-materials/create' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <LipidRawMaterialProvider>
                    <PrivateRoute Component={LipidRawMaterialCreatePage} />
                  </LipidRawMaterialProvider>
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/lipid-raw-materials/edit/:id' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <LipidRawMaterialProvider>
                    <PrivateRoute Component={LipidRawMaterialEditPage} />
                  </LipidRawMaterialProvider>
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/buffers' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <PrivateRoute Component={BuffersListPage} />
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/buffer/create' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <BufferProvider>
                    <PrivateRoute Component={BufferCreatePage} />
                  </BufferProvider>
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/buffer/edit/:id' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <BufferProvider>
                    <PrivateRoute Component={BufferEditPage} />
                  </BufferProvider>
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/payload-raw-materials' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <PrivateRoute Component={PayloadRawMaterialsListPage} />
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/payload-raw-materials/create' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <PayloadRawMaterialProvider>
                    <PrivateRoute Component={PayloadRawMaterialsCreatePage} />
                  </PayloadRawMaterialProvider>
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/payload-raw-materials/edit/:id' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <PayloadRawMaterialProvider>
                    <PrivateRoute Component={PayloadRawMaterialsEditPage} />
                  </PayloadRawMaterialProvider>
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/lipid-stocks' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <PrivateRoute Component={LipidStocksListPage} />
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/lipid-stocks/create' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <LipidStocksProvider>
                    <PrivateRoute Component={LipidStocksCreatePage} />
                  </LipidStocksProvider>
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/lipid-stocks/edit/:id' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <LipidStocksProvider>
                    <PrivateRoute Component={LipidStocksEditPage} />
                  </LipidStocksProvider>
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/payload-phase' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <PrivateRoute Component={PayloadPhaseListPage} />
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/payload-phase/create' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <PayloadPhaseProvider>
                    <PrivateRoute Component={PayloadPhaseCreatePage} />
                  </PayloadPhaseProvider>
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/payload-phase/edit/:id' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <PayloadPhaseProvider>
                    <PrivateRoute Component={PayloadPhaseEditPage} />
                  </PayloadPhaseProvider>
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/encapsulation-details' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <FormulationPickerProvider>
                    <EncapsulantPhaseProvider>
                      <PrivateRoute Component={EncapsulantPhaseCreatePage} />
                    </EncapsulantPhaseProvider>
                  </FormulationPickerProvider>
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/mixing' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <FormulationPickerProvider>
                    <MixingProvider>
                      <PrivateRoute Component={MixingPage} />
                    </MixingProvider>
                  </FormulationPickerProvider>
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/dilution' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <FormulationPickerProvider>
                    <DilutionProvider>
                      <PrivateRoute Component={DilutionCreatePage} />
                    </DilutionProvider>
                  </FormulationPickerProvider>
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/final-lnp-sample' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <FormulationPickerProvider>
                    <FinalLNPSampleProvider>
                      <PrivateRoute Component={FinalLNPSampleCreatePage} />
                    </FinalLNPSampleProvider>
                  </FormulationPickerProvider>
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/database-statistics' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <PrivateRoute Component={DatabaseStatisticsPage} />
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/profile' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <PrivateRoute Component={UserProfilePage} />
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/roles' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <PrivateRoute Component={RoleListPage} />
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/role/create' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <PrivateRoute Component={RoleCreatePage} />
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route path='/role/edit/:id' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <PrivateRoute Component={RoleEditPage} />
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/users' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <PrivateRoute Component={UserListPage} />
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/settings' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <PrivateRoute Component={SettingsPage} />
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/organisations' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <PrivateRoute Component={OrganisationsListPage} />
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route path='/organisation/edit/:id' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <PrivateRoute Component={OrganisationEditPage} />
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/file-types' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <PrivateRoute Component={FileTypeListPage} />
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route path='/file-type/edit/:id' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <PrivateRoute Component={FileTypeEditPage} />
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/logs' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <PrivateRoute Component={LogListPage} />
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route path='/notifications' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <PrivateRoute Component={NotificationPage} />
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route path='/table-metadata' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <PrivateRoute Component={TableListPage} />
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route exact path='/projects' element={
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <DrawerProvider>
                  <PrivateRoute Component={ProjectListPage} />
                </DrawerProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>}
        />

        <Route path="*" element={<PageNotFoundPage />} />
      </Routes>
    </>
  );
}

export default App;